import React, { useMemo } from 'react';
import Button from '../Buttons/Button/Button';
import Input from '../Inputs/Input/Input';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Form from '../Form/Form';
import * as Yup from 'yup';
import { useSumbitFormCallback } from '../Form/submit.hook';
import { validationErrors } from '../../utils/validators';
import { v4 as uuidv4 } from 'uuid';
import { useAddLocationRequestMutation } from '../../redux/api';
import { AnalyticEventsEnum, trackEvent } from '../../Analytics';
import { LocationFullType } from '../Location/config';
import { Stack } from '@mui/material';

import s from './LocationModal.module.scss';

type FormType = { email: string };

const validation = {
  email: Yup.string()
    .email(validationErrors.EMAIL)
    .required(validationErrors.REQUIRED)
    .nullable(),
};

const validationSchema: Yup.SchemaOf<Partial<FormType>> =
  Yup.object().shape(validation);

export const EmailForm = ({
  location,
  setIsSubscribed,
  isSubscribed,
}: {
  location?: LocationFullType;
  setIsSubscribed: () => void;
  isSubscribed: boolean;
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitted },
    reset,
    trigger,
    setValue,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: '',
    } as Partial<FormType>,
  });

  const [addTrigger, addMetadata] = useAddLocationRequestMutation();
  const id = useMemo(() => uuidv4(), []);

  const submitCallback = async ({ email }: FormType) => {
    if (!location) throw new Error('Not possible');

    const result = await addTrigger({
      body: {
        id,
        email,
        location,
      },
    });

    if ('data' in result) {
      setIsSubscribed();
    }

    await trackEvent(AnalyticEventsEnum.LOCATION__EMAIL_SUBMITTED, {
      email,
      location,
      data: 'data' in result ? result.data : undefined,
      error: 'error' in result ? JSON.stringify(result.error) : undefined,
    });
  };

  const { onSubmit } = useSumbitFormCallback<FormType>({
    submitCallback,
    handleSubmit,
    reset,
    trigger,
    setValue,
    isSubmitted,
    hideToast: true,
    trackSubmitError: true,
  });

  return (
    <Form hidePrompt onSubmit={onSubmit} loading={addMetadata.isLoading}>
      <Stack direction="row" alignItems="stretch" spacing={1}>
        <Input
          disabled={isSubscribed}
          autocomplete="email"
          placeholder="Email"
          type="email"
          inputmode="email"
          _name="email"
          _errorText={errors.email?.message}
          _register={register}
        />

        <Button
          disabled={isSubscribed}
          loading={addMetadata.isLoading}
          type="submit"
          className={s.Explore__Button}
          skipCentralizer
          text={isSubscribed ? 'Done' : 'Notify me!'}
          color="primary"
          fill="solid"
        />
      </Stack>
    </Form>
  );
};
